/**
 * Single source of truth for "what language does the user probably want?"
 *
 * Returns a 2-letter ISO 639-1 code that is guaranteed to be in the supplied
 * supported-languages set. Falls back to 'en' if the browser locale is
 * unsupported.
 *
 * Caller is responsible for passing the right supported-set (NLLB-200 has
 * different coverage than the YouTube list, for example).
 *
 * NOTE: Do NOT use localStorage directly in this file — all keys come from
 * STORAGE_KEYS (constants/storage.ts), per RULE #16.
 */

import { STORAGE_KEYS } from '../constants/storage';

// =============================================================================
// Browser detection
// =============================================================================

/**
 * Detect the browser's primary language, mapped to a 2-letter ISO 639-1 code.
 * Falls back to 'en' when the browser locale is not in the supported set.
 */
export function detectBrowserLanguage(supported: ReadonlySet<string>): string {
  if (typeof navigator === 'undefined') return 'en';
  const raw =
    (navigator.languages && navigator.languages.length > 0
      ? navigator.languages[0]
      : navigator.language) || 'en';
  const primary = raw.toLowerCase().split('-')[0]; // 'pt-BR' → 'pt'
  return supported.has(primary) ? primary : 'en';
}

// =============================================================================
// localStorage helpers  (all keys via STORAGE_KEYS — RULE #16)
// =============================================================================

type LanguageKind = 'audio' | 'subtitle';

function storageKey(kind: LanguageKind): string {
  return kind === 'audio'
    ? STORAGE_KEYS.PREFERRED_AUDIO_LANGUAGE
    : STORAGE_KEYS.PREFERRED_SUBTITLE_LANGUAGE;
}

/** Load a previously persisted single language code. Returns null if absent. */
export function loadStoredLanguage(kind: LanguageKind): string | null {
  try {
    return window.localStorage.getItem(storageKey(kind));
  } catch {
    return null;
  }
}

/** Load previously persisted language codes (comma-separated). Returns null if absent. */
export function loadStoredLanguages(kind: LanguageKind): string[] | null {
  try {
    const raw = window.localStorage.getItem(storageKey(kind));
    if (!raw) return null;
    return raw.split(',').filter(Boolean);
  } catch {
    return null;
  }
}

/** Persist a single language code to localStorage. */
export function persistLanguage(kind: LanguageKind, code: string): void {
  try {
    window.localStorage.setItem(storageKey(kind), code);
  } catch {
    /* private mode / storage quota — silently ignore */
  }
}

/** Persist multiple language codes (comma-separated) to localStorage. */
export function persistLanguages(kind: LanguageKind, codes: string[]): void {
  try {
    window.localStorage.setItem(storageKey(kind), codes.join(','));
  } catch {
    /* private mode / storage quota — silently ignore */
  }
}

// =============================================================================
// Resolvers — used as useState initializers
// =============================================================================

/**
 * Resolve the initial single-language value.
 * Priority: localStorage > browser detect > 'en'
 */
export function resolveInitialLanguage(kind: LanguageKind, supported: ReadonlySet<string>): string {
  return loadStoredLanguage(kind) ?? detectBrowserLanguage(supported);
}

/**
 * Resolve the initial multi-language array.
 * Priority: localStorage > [browser detect] > ['en']
 */
export function resolveInitialLanguages(
  kind: LanguageKind,
  supported: ReadonlySet<string>
): string[] {
  return loadStoredLanguages(kind) ?? [detectBrowserLanguage(supported)];
}
